import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, Mail, Phone, MapPin } from "lucide-react";
import { BRAND, NAV_LINKS } from "../data/content";

const Footer = () => {
    return (
        <footer
            data-testid="site-footer"
            className="bg-samora-ink text-samora-bone pt-24 md:pt-32 pb-8"
        >
            <div className="max-w-[1440px] mx-auto px-6 md:px-12">
                {/* Massive brand mark */}
                <div className="mb-20 md:mb-28">
                    <p className="eyebrow text-samora-gold mb-6">
                        The Studio · Est. Whitefield
                    </p>
                    <h2
                        className="font-display leading-[0.9] tracking-tighter text-samora-bone"
                        style={{
                            fontSize: "clamp(3.5rem, 12vw, 12rem)",
                        }}
                    >
                        Samora<span className="text-samora-gold">.</span>
                    </h2>
                    <p className="mt-6 max-w-xl font-serif text-2xl italic text-samora-sand/80">
                        Designing homes that feel like you — one honest room at
                        a time.
                    </p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-16 pb-16 border-t border-samora-bone/15 pt-16">
                    {/* Contact */}
                    <div>
                        <p className="eyebrow text-samora-gold mb-6">Studio</p>
                        <div className="flex items-start gap-3 mb-4">
                            <MapPin
                                size={16}
                                className="text-samora-gold mt-1 flex-shrink-0"
                            />
                            <p className="text-sm text-samora-bone/80 leading-relaxed">
                                {BRAND.address}
                            </p>
                        </div>
                        <a
                            href={`tel:${BRAND.phoneRaw}`}
                            data-testid="footer-phone"
                            className="flex items-center gap-3 mb-3 text-sm text-samora-bone/80 hover:text-samora-gold transition-colors"
                        >
                            <Phone size={16} className="text-samora-gold" />
                            {BRAND.phone}
                        </a>
                        <a
                            href={`mailto:${BRAND.email}`}
                            data-testid="footer-email"
                            className="flex items-center gap-3 text-sm text-samora-bone/80 hover:text-samora-gold transition-colors"
                        >
                            <Mail size={16} className="text-samora-gold" />
                            {BRAND.email}
                        </a>
                    </div>

                    {/* Quick links */}
                    <div>
                        <p className="eyebrow text-samora-gold mb-6">Explore</p>
                        <ul className="space-y-3">
                            {NAV_LINKS.map((l) => (
                                <li key={l.to}>
                                    <Link
                                        to={l.to}
                                        data-testid={`footer-link-${l.label.toLowerCase()}`}
                                        className="text-sm text-samora-bone/80 hover:text-samora-gold transition-colors"
                                    >
                                        {l.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Services */}
                    <div>
                        <p className="eyebrow text-samora-gold mb-6">Studio Practice</p>
                        <ul className="space-y-3 text-sm text-samora-bone/80">
                            <li>Residential Interiors</li>
                            <li>Villas & Farmhouses</li>
                            <li>Modular Kitchens</li>
                            <li>Turnkey Delivery</li>
                            <li>Furniture Design</li>
                            <li>3D Visualization</li>
                        </ul>
                    </div>

                    {/* Social */}
                    <div>
                        <p className="eyebrow text-samora-gold mb-6">Follow</p>
                        <div className="flex gap-4 mb-6">
                            <a
                                href={BRAND.instagram}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="footer-instagram"
                                aria-label="Instagram"
                                className="w-10 h-10 border border-samora-bone/30 flex items-center justify-center hover:bg-samora-gold hover:border-samora-gold transition-colors"
                            >
                                <Instagram size={16} />
                            </a>
                            <a
                                href={BRAND.facebook}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="footer-facebook"
                                aria-label="Facebook"
                                className="w-10 h-10 border border-samora-bone/30 flex items-center justify-center hover:bg-samora-gold hover:border-samora-gold transition-colors"
                            >
                                <Facebook size={16} />
                            </a>
                            <a
                                href={BRAND.youtube}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="footer-youtube"
                                aria-label="YouTube"
                                className="w-10 h-10 border border-samora-bone/30 flex items-center justify-center hover:bg-samora-gold hover:border-samora-gold transition-colors"
                            >
                                <Youtube size={16} />
                            </a>
                        </div>
                        <p className="text-xs text-samora-bone/50 leading-relaxed">
                            For collaborations, press & bespoke commissions
                            write to us.
                        </p>
                    </div>
                </div>

                {/* Map */}
                <div className="mb-10">
                    <iframe
                        data-testid="footer-map"
                        title="Samora Spaces Studio Map"
                        src={BRAND.mapEmbed}
                        className="w-full h-64 grayscale"
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                    ></iframe>
                </div>

                <div className="border-t border-samora-bone/15 pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
                    <p className="text-xs text-samora-bone/50 tracking-wider">
                        © {new Date().getFullYear()} Samora Spaces by Sam. All
                        homes reserved.
                    </p>
                    <p className="text-xs text-samora-bone/50 tracking-wider">
                        {BRAND.domain} · Lux Design Studio, Bengaluru
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
